"use client";

import React, { useEffect, useState } from "react";
import { AgenticToolCallRow } from "@/components/ui/agentic-tool-call-row";
import type { ChatEntitySearchResultsEmbed } from "./types";
import { isDeliverableSearchEmbed } from "./entitySearchResultsUi";
import { SearchEmbedCandidateList } from "./SearchEmbedCandidateList";
import { CHAT_TOOL_STEP_DETAIL_PANEL_CLASS } from "./chatToolStepDetailUi";
import { ToolStepRow, parseToolStep, type ToolStep } from "./ToolStepRow";

type ToolStepSearchEmbedRowProps = {
  toolSegment: Record<string, unknown>;
  searchEmbeds: ChatEntitySearchResultsEmbed[];
  onReplayFeTool?: (toolId: string, params: Record<string, unknown>) => void;
};

function stepFromSegment(toolSegment: Record<string, unknown>): ToolStep | null {
  return parseToolStep(toolSegment);
}

function mapStatus(status?: string) {
  if (status === "error") return "error" as const;
  if (status === "pending") return "pending" as const;
  if (status === "running") return "running" as const;
  if (status === "cancelled") return "cancelled" as const;
  return "success" as const;
}

function displayLabel(step: ToolStep): string {
  return step.label || step.tool_label || step.tool_name;
}

/** One row: tool-step label/summary plus expandable search candidates (no duplicate search summary line). */
export function ToolStepSearchEmbedRow({
  toolSegment,
  searchEmbeds,
  onReplayFeTool,
}: ToolStepSearchEmbedRowProps) {
  const step = stepFromSegment(toolSegment);
  const deliverable = searchEmbeds.some(isDeliverableSearchEmbed);
  const hasCandidates = searchEmbeds.some((embed) => embed.candidates.length > 0);
  const [expanded, setExpanded] = useState(deliverable);

  useEffect(() => {
    setExpanded(deliverable);
  }, [deliverable]);

  if (!step) {
    return null;
  }

  const replayable =
    Boolean(step.fe_tool_id) &&
    step.fe_tool_params &&
    typeof onReplayFeTool === "function";

  const showCandidates = hasCandidates && (deliverable || expanded);
  const candidateDetail = showCandidates ? (
    <div className={CHAT_TOOL_STEP_DETAIL_PANEL_CLASS}>
      <SearchEmbedCandidateList embeds={searchEmbeds} density="toolDetail" />
    </div>
  ) : null;

  if (!hasCandidates) {
    return <ToolStepRow step={step} onReplayFeTool={onReplayFeTool} />;
  }

  const toggleExpand = () => {
    if (!deliverable) {
      setExpanded((open) => !open);
    }
  };

  return (
    <div
      data-testid="chat-tool-step-search-embed"
      data-presentation={deliverable ? "deliverable" : "step"}
      role={replayable || !deliverable ? "button" : undefined}
      tabIndex={replayable || !deliverable ? 0 : undefined}
      aria-expanded={showCandidates}
      className={
        replayable || !deliverable
          ? "cursor-pointer rounded-md outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
          : undefined
      }
      onClick={() => {
        if (replayable && step.fe_tool_id && step.fe_tool_params) {
          onReplayFeTool?.(step.fe_tool_id, step.fe_tool_params);
          return;
        }
        if (!deliverable) {
          toggleExpand();
        }
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }
        event.preventDefault();
        if (replayable && step.fe_tool_id && step.fe_tool_params) {
          onReplayFeTool?.(step.fe_tool_id, step.fe_tool_params);
          return;
        }
        if (!deliverable) {
          toggleExpand();
        }
      }}
    >
      <AgenticToolCallRow
        toolName={step.tool_name}
        label={displayLabel(step)}
        status={mapStatus(step.status)}
        hidePrefix={Boolean(step.kind)}
        summary={step.summary}
        detail={candidateDetail}
        detailClassName="!ml-3"
        className="!pl-0"
        data-testid="chat-tool-step"
      />
    </div>
  );
}
